/* rs-internal-header.js — internal-page header behaviour (every template EXCEPT index).
   Loaded `defer` from snippets/rs-internal-header.liquid; styles in assets/rs-internal-header.css.
   Does three things, all keyed on html.rs-internal (set by layout/theme.liquid):
     1. hide-on-scroll: html.rs-hdr-hidden when scrolling down (>24px past 120px), off when
        scrolling up; never while a header drawer / dialog / predictive search is open; revealed
        when focus lands inside the header. Passive scroll listener coalesced with rAF.
     2. hero overlay: html.rs-hdr-overlay while html.rs-has-hero and any of the .rs-clp-hero is
        still under the header (its BOTTOM edge at or below the header's bottom). Scrolling back up
        inside the hero, or a drawer/picker reveal mid-hero, brings the transparent bar back over the
        photo; the bar goes solid white with the theme logo only once the photo has scrolled past.
        (John 2026-10-13: "the image goes under the header, menu rides on top of image".)
     3. drawer tools: the A–Z row inside the flyout closes the flyout (its focus trap would pull
        focus back out of the picker) and lets the picker's own [data-rsaz-open] handler run. The
        close hands the hamburger trigger to header.drawer.js as the element to focus, which is
        also what makes it tear the focus trap down (with null it skips removeTrapFocus). The row
        carries data-rsaz-return pointing at the trigger, so the picker returns focus there too.
   The internal logo lockup and the combined menu+search glyph are rendered by Liquid
   (snippets/rs-internal-logo.liquid via header.logo-block, assets/theme.menu-search.svg via
   header.icons), not injected here — they used to be, and the page showed the theme's black
   logo over the hero until DOMContentLoaded.
   No globals, no dependencies, nothing touched on the home page (the script is not loaded there).
   PGL-035 (post-go-live audit, 2026-10-27): update() used to read getComputedStyle(html) twice and two
   getBoundingClientRect()s AFTER toggling classes on <html>, so every scroll frame forced a synchronous
   layout (110 ms on /search, a 371 ms long task on /pages/heirloom-expo). Now the header height is cached
   by measure() (resize / ResizeObserver / fonts.ready), every layout read in update() happens before any
   class write, and at scrollY 0 nothing is measured at all - the y=0 state (overlay on when a hero exists,
   header shown, toolbar not stuck) is known without a read, so the load-time update() is free. */
(function () {
  'use strict'
  var html = document.documentElement
  if (!html.classList.contains('rs-internal')) return

  var SHOW_BELOW = 120 // never hidden while scrollY <= this
  var DELTA = 24 // minimum travel in one direction before the state flips
  var HERO_TOP_SLACK = 2 // px of sub-pixel tolerance on the hero's top edge

  var headerSection, siteHeader, trigger, flyout
  var lastY = 0
  var dir = 0 // 1 down, -1 up
  var anchorY = 0
  var ticking = false
  var hidden = false
  var overlay = html.classList.contains('rs-hdr-overlay')
  var hasHero = false
  var hero = null
  var headerH = 56 // cached by measure(); the only consumer of --rs-header-h that used to re-read it per frame

  function setHidden(v) {
    if (hidden === v) return
    hidden = v
    html.classList.toggle('rs-hdr-hidden', v)
  }
  function setOverlay(v) {
    if (overlay === v) return
    overlay = v
    html.classList.toggle('rs-hdr-overlay', v)
  }

  function measure() {
    if (!headerSection) return
    var h = headerSection.offsetHeight
    if (h > 0) {
      headerH = h
      html.style.setProperty('--rs-header-h', h + 'px')
    }
    hasHero = html.classList.contains('rs-has-hero')
    hero = hasHero ? document.querySelector('.rs-clp-hero, [data-rs-hero]') : null // [data-rs-hero] = the PDP photo on phones (PDP 2026 look, 2026-10-21)
    if (hasHero && !hero) hasHero = false
  }

  // Anything open that should keep the header put (and its own fixed/absolute boxes stable).
  function blocked() {
    return !!document.querySelector(
      '.site-header__drawer.is-active, dialog[open], predictive-search.is-active, .modal.modal--is-active, [data-rsaz-dialog][open], body[style*="position: fixed"],' +
      // the phone filter drawer: the theme scrolls the page to the toolbar when it opens, which read as a
      // down-scroll and hid the header AND the toolbar, leaving the drawer with no reachable close control
      ' .collection-mobile-filters .filter-wrapper.is-active, mobile-filters-trigger .is-active'
    )
  }

  // The phone toolbar (.collection-filter) is sticky at top = header height. It must only slide away with the
  // header while it is actually PINNED; while it is still in normal flow (above the fold, next to the
  // description) a translate would drag it over the "Read more" button. Marked every frame from update().
  var toolbar = null

  function update() {
    ticking = false
    var y = window.scrollY
    if (y < 0) y = 0 // iOS rubber-band
    var scrolled = y > 0
    var isBlocked = blocked()

    // ---- reads first (PGL-035): every layout read happens before any class write below, so the frame
    //      never forces a synchronous layout. At y=0 nothing needs measuring at all.
    var heroBottom = null
    if (hasHero && hero && scrolled) heroBottom = hero.getBoundingClientRect().bottom
    if (toolbar === null) toolbar = document.querySelector('.collection-filter') || false
    var toolbarTop = null
    if (toolbar && scrolled) toolbarTop = toolbar.getBoundingClientRect().top

    // 2. overlay: while ANY of the hero photo is still under the header (John 2026-10-13: "the image
    //    goes under the header, menu rides on top of image, so yes transparent header"). The bar goes
    //    solid only once the hero's bottom edge has passed above the header's bottom edge. At y=0 the
    //    hero is by definition still under the header.
    var nextOverlay = false
    if (hasHero && hero) nextOverlay = !scrolled || heroBottom - headerH >= -HERO_TOP_SLACK

    // 1. hide / reveal
    var nextHidden = hidden
    if (isBlocked) {
      nextHidden = false
      lastY = y
      anchorY = y
    } else {
      var d = y - lastY
      if (d > 0 && dir !== 1) {
        dir = 1
        anchorY = y
      } else if (d < 0 && dir !== -1) {
        dir = -1
        anchorY = y
      }
      lastY = y

      if (y <= SHOW_BELOW) nextHidden = false
      else if (dir === 1 && y - anchorY > DELTA) nextHidden = true
      else if (dir === -1 && anchorY - y > DELTA) nextHidden = false
    }

    // ---- writes
    setOverlay(nextOverlay)
    setHidden(nextHidden)
    if (toolbar) {
      var limit = (nextHidden ? 0 : headerH) + 1
      toolbar.classList.toggle('is-stuck', scrolled && toolbarTop !== null && toolbarTop <= limit)
    }
  }

  function requestUpdate() {
    if (ticking) return
    ticking = true
    window.requestAnimationFrame(update)
  }

  function reveal() {
    setHidden(false)
    dir = -1
    anchorY = lastY
  }

  // 3. drawer tools
  function openFlyout() {
    if (trigger) trigger.click()
    else document.dispatchEvent(new CustomEvent('mobileNav:open', { bubbles: true }))
  }

  function onDocumentClickCapture(evt) {
    var t = evt.target
    if (!t || !t.closest) return
    if (t.closest('mobile-filters-trigger')) { reveal(); setTimeout(requestUpdate, 60); setTimeout(requestUpdate, 400) }
    // A leftover search icon in the header row opens the flyout instead of the search drawer
    var searchIcon = t.closest('.site-header .js-search-header')
    if (searchIcon) {
      evt.preventDefault()
      evt.stopPropagation()
      reveal()
      openFlyout()
      return
    }
    // The flyout's A–Z row: close the flyout first (its focus trap would otherwise pull focus
    // back out of the picker), let the click continue to the picker's own handler, and only
    // return focus to the hamburger if nothing took it. activeElement must be a real element:
    // header.drawer.js only calls removeTrapFocus(activeElement) when it is truthy, so null would
    // leave the drawer's focusin trap installed under the open picker.
    var az = t.closest('.site-header__mobile-nav [data-rsaz-open]')
    if (az && flyout && flyout.classList.contains('is-active') && typeof flyout.close === 'function') {
      flyout.activeElement = trigger || null
      flyout.close(null, true)
      window.setTimeout(function () {
        var a = document.activeElement
        if (!a || a === document.body || (flyout && flyout.contains(a))) {
          if (trigger) trigger.focus({ preventScroll: true })
        }
      }, 60)
    }
  }

  // DOM-dependent wiring, re-run when the theme editor re-renders the header section.
  function bind() {
    headerSection = document.querySelector('.header-section')
    siteHeader = document.getElementById('SiteHeader')
    trigger = document.querySelector('.site-header .mobile-nav-trigger')
    flyout = document.querySelector('header-drawer.site-header__mobile-nav')
    if (!headerSection || !siteHeader) return false
    measure()
    return true
  }

  function init() {
    if (!bind()) return
    html.classList.add('rs-ih-ready')

    lastY = window.scrollY > 0 ? window.scrollY : 0
    anchorY = lastY
    update()

    // Only a sticky header can be hidden; a short+wide viewport turns sticky off in CSS and
    // header.nav.js can force position:relative. In that case the scroll logic is skipped but the
    // overlay state still tracks the hero.
    window.addEventListener('scroll', requestUpdate, { passive: true })
    window.addEventListener('resize', function () {
      measure()
      requestUpdate()
    }, { passive: true })
    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        measure()
        requestUpdate()
      }).observe(headerSection)
    }
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(function () {
        measure()
        requestUpdate()
      })
    }

    // Keep the header put while anything opens over the page, and bring it back for it.
    document.addEventListener('headerDrawer:opened', reveal)
    document.addEventListener('cart:open', reveal)
    document.addEventListener('mobileNav:open', reveal)
    document.addEventListener('headerDrawer:closed', function () {
      anchorY = lastY = window.scrollY
    })
    document.addEventListener('focusin', function (evt) {
      if (headerSection.contains(evt.target)) reveal()
    })
    document.addEventListener('click', onDocumentClickCapture, true)

    // Theme editor: a re-rendered header section is a new element to measure and bind.
    document.addEventListener('shopify:section:load', function () {
      bind()
      requestUpdate()
    })

    // The hero section may land its class after us (editor reloads); follow it.
    if ('MutationObserver' in window) {
      new MutationObserver(function () {
        var now = html.classList.contains('rs-has-hero')
        if (now !== hasHero) {
          measure()
          requestUpdate()
        }
      }).observe(html, { attributes: true, attributeFilter: ['class'] })
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init)
  else init()
})()
